import { effect, signal, untracked } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { IServerSideDatasource, IServerSideGetRowsParams } from 'ag-grid-enterprise';
import { catchError, combineLatest, EMPTY, filter, Observable, of, race, Subject, Subscription, take, takeUntil, tap, timer } from 'rxjs';
import { RowsResult, VisualDataResource } from '../../data';
import { VisualDataAdapter } from '../../data/VisualDataAdapter';
import { AgGridTableVisualComponent } from './TableVisual.component';
import { logger } from '../../../utils/logger';
import { VisualField } from '../../VisualField';

const LOGGER = logger('AgGridTableVisualDataSource');

export interface AgGridTableVisualDataRow {
  id: any;
  readonly result: RowsResult;
  readonly values: any;
}

export class AgGridTableVisualDataSource {
  destroyed  = false;
  destroyed$ = new Subject<void>();

  get tableComp() { 
    return this.component as AgGridTableVisualComponent; 
  }

  readonly data     = this.component.data;
  readonly injector = this.component.injector;
  
  get order() {
    return this.tableComp.dataTable.data.orderBy;
  }

  constructor(readonly component: AgGridTableVisualComponent) {
    effect(() => {
      if (this.destroyed)
        return;

      let [table, order] = [this.data.table(), this.order()];
      if (table) {
        untracked(() => this.updateServerSideDatasourceAdapter());
      }
    }, { injector: this.injector });
  }

  destroy() {
    if (this.destroyed)
      return;

    this.destroyed = true;
    this.destroyed$.next();

    this.serverSideDatasourceAdapter()?.destroy();
  }

  readonly serverSideDatasourceAdapter = signal<AgGridTableVisualDataSource.ServerSideDatasourceAdapter>(null);

  private updateServerSideDatasourceAdapter(force?: boolean) {
    let adapter = this.serverSideDatasourceAdapter();

    if (!force) {
      if (adapter && !adapter.destroyed)
        return;
    }

    try {
      this.serverSideDatasourceAdapter.set(
        new AgGridTableVisualDataSource.ServerSideDatasourceAdapter(this, this.order()));
    }
    finally {
      adapter?.destroy();
    }
  }
}

export namespace AgGridTableVisualDataSource {
  export class ServerSideDatasourceAdapter implements IServerSideDatasource {
    destroyed  = false;
    destroyed$ = new Subject<void>();

    private subscriptions = new Subscription();
    private pending = new Set<IServerSideGetRowsParams>();

    constructor(
      readonly dataSource: AgGridTableVisualDataSource,
      readonly order: VisualDataAdapter.OrderByList) { }

    getRows(params: IServerSideGetRowsParams<any, any>) {
      if (this.destroyed)
        return;

      untracked(() => {
        this.pending.add(params);

        try {
          let request = this.getRequest(params);
          
          this.subscriptions ??= new Subscription();
          this.subscriptions.add(this.processRequest(request, params).subscribe());
        }
        catch (err) {
          LOGGER.error(err);

          if (!this.pending.has(params))
            return;

          this.pending.delete(params);

          try {
            params.fail();
          }
          catch (err) {
            LOGGER.error(err);
          }
        }
      });
    }

    private getRequest(params: IServerSideGetRowsParams<any, any>) {
      return this.dataSource.data.rows({
        abortSignal: this.destroyed$,
        debugName: `ServerSideDatasourceAdapter.getRows`,

        skip: params.request.startRow,
        take: params.request.endRow - params.request.startRow,
        order: this.dataSource.order(),
      });
    }

    private processRequest(
      request: VisualDataResource<RowsResult>,
      params: IServerSideGetRowsParams<any, any>) {
      let paramsCompleted = false;

      let destroyed$ = race(this.destroyed$, this.dataSource.destroyed$);

      return combineLatest({
        completed: toObservable(request.completed, { injector: this.dataSource.injector })
      }).pipe(
        takeUntil(destroyed$),
        catchError(err => {
          LOGGER.error(err);

          if (this.destroyed)
            return;

          if (!paramsCompleted) {
            params.fail();
          }

          return EMPTY;
        }),

        filter(({ completed }) => completed),
        take(1),
        takeUntil(destroyed$),

        tap(({ completed }) => {
          if (this.destroyed)
            return;

          if (!this.pending.has(params)) {
            paramsCompleted = true;
            return;
          }

          this.pending.delete(params);

          if (request.success()) {
            let result = request.result();
            let rowData: AgGridTableVisualDataRow[] = Array(result.rows.length);
            
            if (result.rows.length) {
              for (let i = 0; i < result.rows.length; ++i) {
                rowData[i] = {
                  result,
                  values: result.rows[i],
                  id: params.request.startRow + i
                };
              }
            }

            params.success({
              rowData,
              rowCount: request.table.totalRows
            });
          }
          else {
            params.fail();
          }

          paramsCompleted = true;
        })
      );
    }

    destroy() {
      if (this.destroyed)
        return;

      this.destroyed = true;
      this.destroyed$.next();

      try {
        this.subscriptions?.unsubscribe();
        this.subscriptions = null;
      }
      catch (err) {
        LOGGER.error(err);
      }
      finally {
        this.pending.forEach(x => {
          try {
            x.fail();
          }
          catch (err) {
            LOGGER.error(err);
          }
        });

        this.pending.clear();
      }
    }
  }
}
